import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { listExtraColumns } from "../../../../common/components/listExtras";
import { AuthorComposer } from "../../../../api/graphql";
import { AuthorComposerListStore } from "../../store";
import { countryDisplayIdByDemonym } from "../../../country/store/CountryCommonStore";
import { countryDisplayId } from "../../../country/store/CountryCommonStore";
import { provinceStateDisplayId } from "../../../provinceState/store/ProvinceStateCommonStore";
import { authorCollectingSocietyDisplayId } from "../../../authorCollectingSociety/store/AuthorCollectingSocietyCommonStore";
import { diaDeCalendario, formatearDia } from "../../../../common/calendarDate";
import { AuthorPublisherPanel } from "./AuthorPublisherPanel";
import {
  VisibilityIcon,
  EditIcon,
  DeleteForeverIcon,
  ContentCopyIcon,
} from "../../../../icons";
import { TenantSetupGuide } from "../../../tenantProfile/components/common/TenantSetupGuide";

export type GridExpressProps = { listStore?: AuthorComposerListStore };

export const AuthorComposerListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<AuthorComposer>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: AuthorComposer, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          label: langStore.editCommand,
          onClick: (row: AuthorComposer, event?: React.MouseEvent) =>
            store.editRow(row.id, event, "menu"),
          icon: <EditIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("update"),
        },
        {
          label: langStore.deleteCommand,
          onClick: (row: AuthorComposer) => store.deleteRow(row.id),
          icon: <DeleteForeverIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("delete"),
        },
        {
          label: langStore.duplicateCommand,
          onClick: (row: AuthorComposer, event?: React.MouseEvent) =>
            store.duplicateRow(row.id, event, "menu"),
          icon: <ContentCopyIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("create"),
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<AuthorComposer>[];
      const mappedColumns = storeColumns.map((col) => {
        if (col.accessorKey === "gender") {
          return {
            ...col,
            id: "gender",
            accessorFn: (row: AuthorComposer) => {
              const v = (row as any).gender;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.GenderOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "taxIdType") {
          return {
            ...col,
            id: "taxIdType",
            accessorFn: (row: AuthorComposer) => {
              const v = (row as any).taxIdType;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.TaxIdTypeOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "cuitCertificateIssuedAt") {
          return {
            ...col,
            id: "cuitCertificateIssuedAt",
            accessorFn: (row: AuthorComposer) =>
              row.cuitCertificateIssuedAt
                ? new Date(row.cuitCertificateIssuedAt).toLocaleString(
                    "es-ES",
                    {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    }
                  )
                : "-",
          };
        }
        if (col.accessorKey === "cuitCertificateErrorType") {
          return {
            ...col,
            id: "cuitCertificateErrorType",
            accessorFn: (row: AuthorComposer) => {
              const v = (row as any).cuitCertificateErrorType;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.CuitCertificateErrorTypeOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "dniDeliveredToSadaicAt") {
          return {
            ...col,
            id: "dniDeliveredToSadaicAt",
            accessorFn: (row: AuthorComposer) =>
              row.dniDeliveredToSadaicAt
                ? new Date(row.dniDeliveredToSadaicAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "dniValidatedAt") {
          return {
            ...col,
            id: "dniValidatedAt",
            accessorFn: (row: AuthorComposer) =>
              row.dniValidatedAt
                ? new Date(row.dniValidatedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "nationality") {
          return {
            ...col,
            id: "nationality",
            accessorFn: (row: AuthorComposer) =>
              row.nationality
                ? countryDisplayIdByDemonym(row.nationality, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "dateOfBirth") {
          return {
            ...col,
            id: "dateOfBirth",
            accessorFn: (row: AuthorComposer) =>
              formatearDia(
                diaDeCalendario(row.dateOfBirth),
                langStore.timeFormat
              ) || "-",
          };
        }
        if (col.accessorKey === "country") {
          return {
            ...col,
            id: "country",
            accessorFn: (row: AuthorComposer) =>
              row.country ? countryDisplayId(row.country, langStore) : "-",
          };
        }
        if (col.accessorKey === "province") {
          return {
            ...col,
            id: "province",
            accessorFn: (row: AuthorComposer) =>
              row.province
                ? provinceStateDisplayId(row.province, langStore)
                : "-",
          };
        }
        if (col.accessorKey === "managementSocieties") {
          return {
            ...col,
            id: "managementSocieties",
            accessorFn: (row: AuthorComposer) =>
              row.managementSocieties?.length
                ? row.managementSocieties
                    .map((x: any) =>
                      authorCollectingSocietyDisplayId(x, langStore)
                    )
                    .join("\n")
                : "-",
          };
        }
        if (col.accessorKey === "publishers") {
          return {
            ...col,
            id: "publishers",
            accessorFn: (row: AuthorComposer) =>
              row.publishers?.length ? `${row.publishers.length} item(s)` : "-",
          };
        }
        if (col.accessorKey === "createdAt_") {
          return {
            ...col,
            id: "createdAt_",
            accessorFn: (row: AuthorComposer) =>
              row.createdAt_
                ? new Date(row.createdAt_).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "lastUpdatedAt_") {
          return {
            ...col,
            id: "lastUpdatedAt_",
            accessorFn: (row: AuthorComposer) =>
              row.lastUpdatedAt_
                ? new Date(row.lastUpdatedAt_).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        return col;
      });
      // Las columnas que el proyecto suma a este listado (common/components/listExtras.tsx), al final.
      const extraColumns = (listExtraColumns["AuthorComposer"]?.(langStore) ??
        []) as DataGridColumn<AuthorComposer>[];
      return [...mappedColumns, ...extraColumns];
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.authorComposerCurrentPage || [];

    return (
      <Box id={"ListOfAuthorComposer"}>
        <TenantSetupGuide />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
          enableExpanding
          renderDetailPanel={({ row }) => (
            <Box sx={{ pl: 4, pr: 2, pb: 2, backgroundColor: "action.hover" }}>
              <AuthorPublisherPanel parentId={row.original.id} />
            </Box>
          )}
        />
      </Box>
    );
  }
);
